import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { SaveButton, useNotify, useRedirect } from "react-admin";
import { useForm, useFormState } from "react-final-form";

import { getCustomFieldsToSave } from "../../../components/common/CustomFields";
import { ConditionalConfirm } from "../../../components/common/ConditionalConfirm";
import { logError } from "../../../utils/appUtils";
import {
  FilePaths,
  NoteType,
  OutboundStatus,
  VALIDATION_MSG,
} from "../../../utils/constants";
import { saveRemoveFile, saveRemoveFiles } from "../../../utils/fileUtils";
import {
  areTimeslotsEqual,
  validateAppointment,
} from "../../../utils/dateUtils";
import { isUserFacingError } from "../../../utils/lambdaErrors";
import { fetchShipWithMembersCached, isLinked } from "../../../utils/shipWith";
import {
  hasAppointmentChanged,
  linkedAppointmentMessage,
} from "../../../utils/shipWithSchedule";
import {
  deepRedirect,
  historyAutoGen,
  normalizeValue,
} from "../../../utils/utils";

export const DialogSaveButton = ({
  record,
  backPath,
  selectedTimeslot,
  isSaving,
  setIsSaving,
  isDeleting,
  ...props
}) => {
  const form = useForm();
  const history = useHistory();
  const notify = useNotify();
  const redirect = useRedirect();
  const { pristine, submitting } = useFormState({ subscription: { pristine: true, submitting: true } });
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  // The dialog is a notice when a slot went (no way forward but to pick
  // again) and a real confirmation when a linked load's appointment is about
  // to move its siblings too; this decides which buttons it shows.
  const [confirmable, setConfirmable] = useState(false);

  const formValidate = () => {
    const newFormState = form.getState();
    if (newFormState.invalid) notify(VALIDATION_MSG, "warning");
  };

  const handleValidateSave = async () => {
    let tempMsg = null;

    if (selectedTimeslot) {
      if (!areTimeslotsEqual(record.timeslot, selectedTimeslot)) {
        const error = await validateAppointment(
          selectedTimeslot,
          record.timeslot?.id,
        );
        if (error) {
          tempMsg = `${selectedTimeslot.time} is no longer available. Please select a new time.`;
        }
      }
    }

    if (tempMsg) {
      setConfirmable(false);
      setMessage(tempMsg);
      setOpen(true);
    } else if (
      isLinked(record) &&
      hasAppointmentChanged(
        record,
        form.getState().values.scheduledDateTime,
        selectedTimeslot,
      )
    ) {
      // A linked load's appointment is the whole truck's (AD-3): say so
      // before the save, naming the loads that move with it. The members come
      // through the badge's shared cache and the read never rejects; a member
      // that failed to load leaves the count standing and its PO off the list.
      const members = await fetchShipWithMembersCached(record.shipWithIds);
      setConfirmable(true);
      setMessage(linkedAppointmentMessage(record, members));
      setOpen(true);
    } else handleSave();
  };

  const handleDialogClose = () => setOpen(false);

  const handleSave = () => {
    formValidate();
    setOpen(false);
    props.handleSubmitWithRedirect();
  };

  const transform = async (data) => {
    setIsSaving(true);
    data = { ...data };

    // Set new Outbound Status & State
    data.status = OutboundStatus.ADVICE_PENDING;
    data.state = "";
    let statusNote = null;
    const newStatus = data.status;
    const oldStatus = form.getState().initialValues.status;

    if (newStatus !== oldStatus) {
      statusNote = {
        text: `Advice submitted | status: ${data.status}`,
        by: window.appUser?.fullName,
        date: new Date(),
        type: NoteType.SHIPMENT,
      };

      data.history = [...data.history, statusNote];
    }

    data.customFields = getCustomFieldsToSave(
      data.customFields,
      data.account.outboundCustomFields,
    );

    const ignoreList = [
      "status",
      "carrierType",
      "transferToAccoundId",
      "createdAt",
    ];
    const includeArrayList = ["customFields"];
    const includeObjectList = ["shipTo"];
    data.history = historyAutoGen(
      form,
      data,
      ignoreList,
      includeArrayList,
      includeObjectList,
    );

    data.poNumber = data.poNumber ? data.poNumber : "";
    data.consigneePoNumber = data.consigneePoNumber
      ? data.consigneePoNumber
      : "";
    data.pickupNumber = data.pickupNumber ? data.pickupNumber : "";

    data.normalizedPoNumber = normalizeValue(data.poNumber);
    data.normalizedConsigneePoNumber = normalizeValue(data.consigneePoNumber);
    data.normalizedPickupNumber = normalizeValue(data.pickupNumber);
    data.normalizedRailCarNumber = normalizeValue(data.railCarNumber);

    if (data.address?.state)
      data.address.state = data.address.state.toUpperCase();
    if (data.address?.zipCode)
      data.address.zipCode = data.address.zipCode.toUpperCase();

    if (data.notes?.length > 0) {
      data.notes.forEach((element) => {
        if (!element.date) {
          element.type = NoteType.GENERIC;
          element.date = new Date();
          element.by = window.appUser?.fullName;
        }
      });
    } else data.notes = undefined;

    // The picked appointment travels with the save; the lambda derives the
    // Timeslot op from it and the stored record.
    data.timeslot = selectedTimeslot;

    try {
      const promises = [
        saveRemoveFiles(
          record,
          data,
          "attachments",
          `${FilePaths.OUTBOUND}/${Date.now()}`,
        ),
        saveRemoveFile(
          record,
          data,
          "bolFile",
          `${FilePaths.OUTBOUND}/${Date.now()}`,
        ),
      ];
      await Promise.all(promises);
    } catch (err) {
      logError("outbounds:DialogSaveButton:transform", err, notify, redirect);
      setIsSaving(false);
      return Promise.reject();
    }

    // NOTE: no sanitizeOutbound here — the dataProvider's outboundShipments
    // branch sanitizes on return; updateLambdaOutbound's child-diffing needs
    // the unsanitized record (shipmentLineItems.items, etc.).

    // clear extra form fields
    delete data.customFieldSelect;
    delete data.totalQuantity;
    // data.saveAddress is kept: consumed by updateLambdaOutbound in the
    // dataProvider branch (addressCreateInput), stripped there afterwards.

    // clear inventorySearchDialog filters
    delete data.lotType;
    delete data.searchArea;
    delete data.searchTerm;
    delete data.showAll;

    // AppSync auto-stamped updatedAt; the direct lambda write does not.
    data.updatedAt = new Date().toISOString();
    // Carry save context to the dataProvider's outboundShipments branch. Its
    // presence is the opt-in gate: the parent record, the pick list, the
    // Timeslot and — for a linked load — every sibling's matching move go into
    // one lambda transaction, instead of an appointment write that commits
    // before the advice it belongs to (AD-6).
    data.__saveContext = { isAdvance: false, isRollback: false };
    return data;
  };

  const onSuccess = (data) => {
    notify("Outbound updated successfully", "success");
    deepRedirect(history, backPath);
    setIsSaving(false);
  };

  const onFailure = (error) => {
    setIsSaving(false);
    if (isUserFacingError(error)) {
      // The lambda names its refusals for the person saving — the slot went, a
      // sibling moved on while the form was open — so those read verbatim, as
      // the carrier dispatch save shows them. Still recorded, without the
      // generic toast on top.
      notify(error.message, "warning");
      logError("outbound:DialogSaveButton:onFailure", error, false);
      return;
    }
    logError("outbound:DialogSaveButton:onFailure", error, notify);
  };

  return (
    <>
      <SaveButton
        {...props}
        label="Submit"
        submitOnEnter={false}
        undoable={false}
        disabled={pristine || isSaving || isDeleting}
        saving={isSaving || submitting}
        handleSubmitWithRedirect={handleValidateSave}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <ConditionalConfirm
        isOpen={open}
        loading={isSaving}
        title="Save Confirmation"
        content={message}
        onConfirm={confirmable ? handleSave : undefined}
        onClose={handleDialogClose}
      />
    </>
  );
};
